import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { formatMoney } from '@/lib/money'

import { AdminHeader } from '../AdminLayout'
import { CollectionEditor, FormActions } from '../components/CollectionEditor'
import {
  Field,
  I18nField,
  MoneyField,
  NumberField,
  SelectField,
  SwitchField,
  TextField,
  type I18nValue,
} from '../components/fields'
import { MediaField } from '../components/MediaField'
import { useAdminContext } from '../session'
import { useCollection } from '../useCollection'
import type {
  DrinkCategory,
  DrinkItem,
  I18n,
  PhysicalTable,
  PhysicalTableStatus,
  PricingStrategy,
  TablePackage,
  TableType,
} from '../types'

/**
 * Boissons, types de tables et plan de salle.
 *
 * Trois choses très différentes se règlent ici, et l'ordre compte :
 *
 * 1. **Les boissons** — le catalogue des bouteilles, avec leur prix.
 * 2. **Les types de tables** — ce qu'on vend : « THE SQUAD, 3-4 personnes,
 *    100 €, deux bouteilles au choix comprises ».
 * 3. **Les formules** — des compositions à prix fixe proposées sur un type
 *    de table (« Formule Champagne : 2 bouteilles, 250 € »).
 * 4. **Le plan de salle** — les tables qui existent physiquement. Il sert au
 *    placement, le soir venu : attribuer une table à qui arrive, valider le
 *    numéro saisi depuis le QR posé sur la table. Il **ne limite pas** la
 *    vente : c'est « Nombre de tables en vente », sur le type de table, qui la
 *    plafonne (`availability_service.remaining_tables`). L'écran le disait à
 *    l'envers, et poussait à dessiner la salle un mois trop tôt.
 */
export function AdminDrinksPage() {
  const { t, i18n } = useTranslation()
  const { eventId, event } = useAdminContext()
  const currency = event?.currency ?? 'EUR'

  useEffect(() => {
    document.title = t('admin.section.drinks')
  }, [t])

  const categories = useCollection<DrinkCategory>({
    key: 'admin-drink-categories',
    path: `/admin/events/${eventId}/drinks/categories`,
    eventId,
    related: [['drinks']],
  })
  const drinks = useCollection<DrinkItem>({
    key: 'admin-drinks',
    path: `/admin/events/${eventId}/drinks/items`,
    eventId,
    related: [['drinks'], ['admin-table-types']],
  })
  const tableTypes = useCollection<TableType>({
    key: 'admin-table-types',
    path: `/admin/events/${eventId}/table-types`,
    eventId,
    related: [['drinks']],
  })
  const packages = useCollection<TablePackage>({
    key: 'admin-table-packages',
    path: `/admin/events/${eventId}/table-packages`,
    eventId,
    related: [['drinks']],
  })
  const physical = useCollection<PhysicalTable>({
    key: 'admin-physical-tables',
    path: `/admin/events/${eventId}/physical-tables`,
    eventId,
    related: [['drinks'], ['admin-readiness', eventId ?? '']],
  })

  function label(value: I18n): string {
    return value[i18n.language] || value[Object.keys(value)[0] ?? ''] || '—'
  }

  function price(minor: number): string {
    return formatMoney({ amount_minor: minor, currency }, i18n.language)
  }

  // Combien de tables physiques portent ce type : une information de
  // placement, pas un stock — la vente se plafonne sur le type lui-même.
  function physicalCount(typeId: string): number {
    return physical.items.filter((table) => table.table_type_id === typeId).length
  }

  return (
    <>
      <AdminHeader
        title={t('admin.section.drinks')}
        description={t('admin.drinks.description')}
      />

      <div className="grid gap-6">
        <CollectionEditor<DrinkCategory>
          title={t('admin.drinks.categories')}
          items={categories.items}
          busy={categories.busy}
          error={categories.error}
          emptyLabel={t('admin.drinks.noCategories')}
          createLabel={t('admin.drinks.addCategory')}
          // Refusée par le serveur tant qu'une boisson y est rangée : le message
          // dit combien en déplacer d'abord.
          onDelete={(category) => categories.remove.mutate({ id: category.id })}
          onReorder={(ids) => categories.reorder.mutate({ ids })}
          summarize={(category) => ({
            title: label(category.name_i18n),
            dimmed: !category.is_visible,
          })}
          renderForm={(category, close) => (
            <SimpleI18nForm
              initialName={category?.name_i18n ?? {}}
              initialVisible={category?.is_visible ?? true}
              busy={categories.busy}
              onCancel={close}
              onSubmit={(payload) => {
                const done = { onSuccess: () => close() }
                if (category) categories.update.mutate({ id: category.id, ...payload }, done)
                else categories.create.mutate(payload, done)
              }}
            />
          )}
        />

        <CollectionEditor<DrinkItem>
          title={t('admin.drinks.items')}
          description={t('admin.drinks.itemsHint')}
          items={drinks.items}
          busy={drinks.busy}
          error={drinks.error}
          emptyLabel={t('admin.drinks.noItems')}
          createLabel={t('admin.drinks.addItem')}
          onDelete={(drink) => drinks.remove.mutate({ id: drink.id })}
          onReorder={(ids) => drinks.reorder.mutate({ ids })}
          summarize={(drink) => ({
            title: label(drink.name_i18n),
            subtitle: [drink.brand, drink.size_label].filter(Boolean).join(' · '),
            value: price(drink.price_minor),
            dimmed: !drink.is_active,
            badges: [
              ...(drink.is_extra ? [{ label: t('admin.drinks.extra') }] : []),
              ...(drink.order_only
                ? [{ label: t('admin.drinks.onOrder'), tone: 'success' as const }]
                : []),
            ],
          })}
          renderForm={(drink, close) => (
            <DrinkForm
              drink={drink}
              categories={categories.items}
              categoryLabel={label}
              currency={currency}
              eventId={eventId}
              busy={drinks.busy}
              onCancel={close}
              onSubmit={(payload) => {
                const done = { onSuccess: () => close() }
                if (drink) drinks.update.mutate({ id: drink.id, ...payload }, done)
                else drinks.create.mutate(payload, done)
              }}
            />
          )}
        />

        <CollectionEditor<TableType>
          title={t('admin.drinks.tableTypes')}
          description={t('admin.drinks.tableTypesHint')}
          items={tableTypes.items}
          busy={tableTypes.busy}
          error={tableTypes.error}
          emptyLabel={t('admin.drinks.noTableTypes')}
          createLabel={t('admin.drinks.addTableType')}
          onDelete={(type) => tableTypes.remove.mutate({ id: type.id })}
          onReorder={(ids) => tableTypes.reorder.mutate({ ids })}
          summarize={(type) => {
            const count = physicalCount(type.id)
            return {
              title: label(type.name_i18n),
              subtitle: t('admin.drinks.capacityRange', {
                min: type.capacity_min,
                max: type.capacity_max,
              }),
              value: price(type.base_price_minor),
              dimmed: !type.is_active,
              badges: [
                // Ce qui limite réellement la vente : le nombre de tables en
                // vente du type, pas le plan de salle.
                type.stock_quantity === null
                  ? { label: t('admin.drinks.onSaleUnlimited') }
                  : {
                      label: t('admin.drinks.onSaleCount', {
                        sold: type.sold_count,
                        count: type.stock_quantity,
                      }),
                      tone:
                        type.sold_count >= type.stock_quantity
                          ? ('warning' as const)
                          : ('neutral' as const),
                    },
                // Le plan de salle, à titre d'information : il sert au
                // placement le soir venu, son absence n'empêche aucune vente.
                ...(count > 0 ? [{ label: t('admin.drinks.physicalCount', { count }) }] : []),
              ],
            }
          }}
          renderForm={(type, close) => (
            <TableTypeForm
              type={type}
              drinks={drinks.items}
              drinkLabel={label}
              currency={currency}
              eventId={eventId}
              busy={tableTypes.busy}
              onCancel={close}
              onSubmit={(payload) => {
                const done = { onSuccess: () => close() }
                if (type) tableTypes.update.mutate({ id: type.id, ...payload }, done)
                else tableTypes.create.mutate(payload, done)
              }}
            />
          )}
        />

        <CollectionEditor<TablePackage>
          title={t('admin.drinks.packages')}
          description={t('admin.drinks.packagesHint')}
          items={packages.items}
          busy={packages.busy}
          error={packages.error}
          emptyLabel={t('admin.drinks.noPackages')}
          // Sans type de table, une formule n'a nulle part où s'afficher.
          createLabel={tableTypes.items.length > 0 ? t('admin.drinks.addPackage') : null}
          // Le serveur refuse si la formule a déjà été vendue : on la
          // désactive alors plutôt que d'effacer ce qu'un client a acheté.
          onDelete={(entry) => packages.remove.mutate({ id: entry.id })}
          onReorder={(ids) => packages.reorder.mutate({ ids })}
          summarize={(entry) => ({
            title: label(entry.name_i18n),
            subtitle: [
              label(
                tableTypes.items.find((type) => type.id === entry.table_type_id)?.name_i18n ?? {},
              ),
              entry.items.map((line) => `${line.quantity} × ${line.name}`).join(', '),
            ]
              .filter(Boolean)
              .join(' · '),
            value: price(entry.package_price_minor),
            dimmed: !entry.is_active,
            badges: [
              ...(entry.max_quantity !== null
                ? [
                    {
                      label: t('admin.drinks.onSaleCount', {
                        sold: entry.sold_count,
                        count: entry.max_quantity,
                      }),
                    },
                  ]
                : []),
              ...(entry.is_active
                ? []
                : [{ label: t('admin.drinks.packageInactive'), tone: 'warning' as const }]),
            ],
          })}
          renderForm={(entry, close) => (
            <PackageForm
              entry={entry}
              tableTypes={tableTypes.items}
              drinks={drinks.items}
              nameOf={label}
              currency={currency}
              busy={packages.busy}
              onCancel={close}
              onSubmit={(payload) => {
                const done = { onSuccess: () => close() }
                if (entry) packages.update.mutate({ id: entry.id, ...payload }, done)
                else packages.create.mutate(payload, done)
              }}
            />
          )}
        />

        <CollectionEditor<PhysicalTable>
          title={t('admin.drinks.floorPlan')}
          description={t('admin.drinks.floorPlanHint')}
          items={physical.items}
          busy={physical.busy}
          error={physical.error}
          emptyLabel={t('admin.drinks.noTables')}
          createLabel={t('admin.drinks.addTable')}
          onDelete={(table) => physical.remove.mutate({ id: table.id })}
          onReorder={(ids) => physical.reorder.mutate({ ids })}
          summarize={(table) => ({
            // Le numéro et le nom ensemble : le service appelle une table par
            // son nom, le plan et les QR par son numéro.
            title: table.name ? `${table.table_number} · ${table.name}` : table.table_number,
            subtitle: [
              table.zone,
              table.table_type_id
                ? label(
                    tableTypes.items.find((type) => type.id === table.table_type_id)?.name_i18n ??
                      {},
                  )
                : t('admin.drinks.noType'),
            ]
              .filter(Boolean)
              .join(' · '),
            value: t('admin.drinks.seats', { count: table.capacity }),
            dimmed: table.status !== 'available',
            badges:
              table.status === 'available'
                ? []
                : [{ label: t(`admin.drinks.status.${table.status}`), tone: 'warning' } as const],
          })}
          renderForm={(table, close) => (
            <PhysicalTableForm
              table={table}
              tableTypes={tableTypes.items}
              typeLabel={label}
              busy={physical.busy}
              onCancel={close}
              onSubmit={(payload) => {
                const done = { onSuccess: () => close() }
                if (table) physical.update.mutate({ id: table.id, ...payload }, done)
                else physical.create.mutate(payload, done)
              }}
            />
          )}
        />
      </div>
    </>
  )
}

// ── Formulaires ────────────────────────────────────────────────────

function SimpleI18nForm({
  initialName,
  initialVisible,
  busy,
  onSubmit,
  onCancel,
}: {
  initialName: I18n
  initialVisible: boolean
  busy: boolean
  onSubmit: (payload: Record<string, unknown>) => void
  onCancel: () => void
}) {
  const { t } = useTranslation()
  const [name, setName] = useState<I18nValue>(initialName)
  const [visible, setVisible] = useState(initialVisible)

  return (
    <div>
      <div className="grid gap-5 sm:grid-cols-2">
        <I18nField label={t('admin.food.name')} value={name} onChange={setName} />
        <SwitchField label={t('admin.food.visible')} value={visible} onChange={setVisible} />
      </div>
      <FormActions
        busy={busy}
        onCancel={onCancel}
        onSubmit={() => onSubmit({ name_i18n: name, is_visible: visible })}
      />
    </div>
  )
}

function DrinkForm({
  drink,
  categories,
  categoryLabel,
  currency,
  eventId,
  busy,
  onSubmit,
  onCancel,
}: {
  drink: DrinkItem | null
  categories: DrinkCategory[]
  categoryLabel: (value: I18n) => string
  currency: string
  eventId: string | undefined
  busy: boolean
  onSubmit: (payload: Record<string, unknown>) => void
  onCancel: () => void
}) {
  const { t } = useTranslation()
  const [name, setName] = useState<I18nValue>(drink?.name_i18n ?? {})
  const [categoryId, setCategoryId] = useState(drink?.category_id ?? '')
  const [brand, setBrand] = useState(drink?.brand ?? '')
  const [size, setSize] = useState(drink?.size_label ?? '')
  const [priceMinor, setPriceMinor] = useState<number | null>(drink?.price_minor ?? null)
  const [unitCostMinor, setUnitCostMinor] = useState<number | null>(
    drink?.unit_cost_minor ?? null,
  )
  const [stock, setStock] = useState<number | null>(drink?.stock_quantity ?? null)
  const [isExtra, setIsExtra] = useState(drink?.is_extra ?? false)
  const [orderOnly, setOrderOnly] = useState(drink?.order_only ?? false)
  const [active, setActive] = useState(drink?.is_active ?? true)
  const [mediaId, setMediaId] = useState<string | null>(drink?.media_id ?? null)

  return (
    <div>
      <div className="grid gap-5 sm:grid-cols-2">
        <I18nField label={t('admin.food.name')} value={name} onChange={setName} />
        <SelectField
          label={t('admin.food.category')}
          value={categoryId}
          onChange={setCategoryId}
          options={[
            { value: '', label: t('admin.food.noCategory') },
            ...categories.map((category) => ({
              value: category.id,
              label: categoryLabel(category.name_i18n),
            })),
          ]}
        />
        <TextField label={t('admin.drinks.brand')} value={brand} onChange={setBrand} />
        <TextField
          label={t('admin.drinks.size')}
          hint={t('admin.drinks.sizeHint')}
          value={size}
          onChange={setSize}
        />
        <MoneyField
          label={t('admin.food.price')}
          minor={priceMinor}
          currency={currency}
          onChange={setPriceMinor}
        />
        {/* Ce que l'unité coûte, par opposition à ce qu'elle rapporte.
            Facultatif : laissé vide, le produit sort de la valeur de la
            réserve au lieu d'y compter pour zéro. */}
        <MoneyField
          label={t('admin.food.unitCost')}
          hint={t('admin.food.unitCostHint')}
          minor={unitCostMinor}
          currency={currency}
          onChange={setUnitCostMinor}
        />
        <NumberField
          label={t('admin.food.stock')}
          hint={t('admin.food.stockHint')}
          value={stock}
          min={0}
          onChange={setStock}
        />
        <SwitchField
          label={t('admin.drinks.isExtra')}
          hint={t('admin.drinks.isExtraHint')}
          value={isExtra}
          onChange={setIsExtra}
        />
        {/* À ne pas confondre avec « extra » juste au-dessus, qui range les
            softs dans le composeur de table. Celui-ci sort la bouteille des
            tables et la met dans la carte à commander. */}
        <SwitchField
          label={t('admin.drinks.orderOnly')}
          hint={t('admin.drinks.orderOnlyHint')}
          value={orderOnly}
          onChange={setOrderOnly}
        />
        <SwitchField
          label={t('admin.active')}
          hint={t('admin.activeHint')}
          value={active}
          onChange={setActive}
        />
        <div className="sm:col-span-2">
          <MediaField
            label={t('admin.media.photo')}
            value={mediaId}
            eventId={eventId}
            onChange={setMediaId}
          />
        </div>
      </div>
      <FormActions
        busy={busy}
        onCancel={onCancel}
        onSubmit={() =>
          onSubmit({
            name_i18n: name,
            media_id: mediaId,
            category_id: categoryId || null,
            brand: brand || null,
            size_label: size || null,
            price_minor: priceMinor ?? 0,
            unit_cost_minor: unitCostMinor,
            stock_quantity: stock,
            is_extra: isExtra,
            order_only: orderOnly,
            is_active: active,
          })
        }
      />
    </div>
  )
}

function TableTypeForm({
  type,
  drinks,
  drinkLabel,
  currency,
  eventId,
  busy,
  onSubmit,
  onCancel,
}: {
  type: TableType | null
  drinks: DrinkItem[]
  drinkLabel: (value: I18n) => string
  currency: string
  eventId: string | undefined
  busy: boolean
  onSubmit: (payload: Record<string, unknown>) => void
  onCancel: () => void
}) {
  const { t } = useTranslation()
  const [name, setName] = useState<I18nValue>(type?.name_i18n ?? {})
  const [description, setDescription] = useState<I18nValue>(type?.description_i18n ?? {})
  const [capacityMin, setCapacityMin] = useState<number | null>(type?.capacity_min ?? 2)
  const [capacityMax, setCapacityMax] = useState<number | null>(type?.capacity_max ?? 4)
  // Combien de tables de cette formule se vendent. Vide = sans limite.
  const [stock, setStock] = useState<number | null>(type?.stock_quantity ?? null)
  const [strategy, setStrategy] = useState<PricingStrategy>(
    type?.pricing_strategy ?? 'base_price',
  )
  const [basePrice, setBasePrice] = useState<number | null>(type?.base_price_minor ?? null)
  const [allowance, setAllowance] = useState<number | null>(
    type?.included_bottles_allowance ?? 0,
  )
  const [minSpend, setMinSpend] = useState<number | null>(type?.min_spend_minor ?? null)
  // RULE 5 : une table ne donne pas l'entrée, sauf décision explicite. La
  // case partait cochée, à rebours du serveur et de l'aide affichée dessous.
  const [admission, setAdmission] = useState(type?.includes_admission ?? false)
  const [active, setActive] = useState(type?.is_active ?? true)
  const [mediaId, setMediaId] = useState<string | null>(type?.media_id ?? null)
  const [included, setIncluded] = useState<Record<string, number>>(() =>
    Object.fromEntries(
      (type?.included_items ?? []).map((line) => [line.drink_item_id, line.quantity]),
    ),
  )
  // L'ordre d'affichage de la composition, table par table. Le serveur le lit
  // dans l'ordre où on lui envoie les lignes : ranger la liste ici suffit,
  // personne ne saisit de numéro. Une boisson cochée arrive en bas ; on la
  // remonte si elle doit ouvrir la carte.
  const [order, setOrder] = useState<string[]>(() =>
    (type?.included_items ?? []).map((line) => line.drink_item_id),
  )

  function setQuantity(drinkId: string, quantity: number) {
    setIncluded((current) => ({ ...current, [drinkId]: quantity }))
    setOrder((current) =>
      quantity > 0
        ? current.includes(drinkId)
          ? current
          : [...current, drinkId]
        : current.filter((id) => id !== drinkId),
    )
  }

  function move(drinkId: string, by: -1 | 1) {
    setOrder((current) => {
      const from = current.indexOf(drinkId)
      const to = from + by
      if (from < 0 || to < 0 || to >= current.length) return current
      const next = [...current]
      next.splice(to, 0, ...next.splice(from, 1))
      return next
    })
  }

  // Ce qui est réellement composé, dans l'ordre voulu.
  const composition = order.filter((id) => (included[id] ?? 0) > 0)

  // Ce que le calcul lit réellement, selon le mode (`table_pricing.py`) :
  // la franchise « N au choix » n'existe qu'en « Bouteilles comprises », la
  // composition d'office qu'en « Bouteilles comprises » ou « Composition
  // libre ». Les afficher ailleurs laissait croire qu'ils comptaient. Les
  // valeurs saisies restent enregistrées : repasser au bon mode les retrouve.
  const usesAllowance = strategy === 'included_items'
  const usesIncluded = strategy === 'included_items' || strategy === 'custom_combination'

  return (
    <div>
      <div className="grid gap-5 sm:grid-cols-2">
        <I18nField label={t('admin.food.name')} value={name} onChange={setName} />
        <I18nField
          label={t('admin.food.descriptionField')}
          value={description}
          onChange={setDescription}
        />
        <NumberField
          label={t('admin.drinks.capacityMin')}
          value={capacityMin}
          min={1}
          max={50}
          onChange={setCapacityMin}
        />
        <NumberField
          label={t('admin.drinks.capacityMax')}
          value={capacityMax}
          min={1}
          max={50}
          onChange={setCapacityMax}
        />
        <NumberField
          label={t('admin.drinks.tableStock')}
          hint={t('admin.drinks.tableStockHint')}
          value={stock}
          min={0}
          max={500}
          onChange={setStock}
        />
        <SelectField<PricingStrategy>
          label={t('admin.drinks.strategyLabel')}
          hint={t('admin.drinks.strategyHint')}
          value={strategy}
          onChange={setStrategy}
          options={[
            { value: 'base_price', label: t('admin.drinks.strategy.base_price') },
            { value: 'included_items', label: t('admin.drinks.strategy.included_items') },
            { value: 'package_price', label: t('admin.drinks.strategy.package_price') },
            {
              value: 'custom_combination',
              label: t('admin.drinks.strategy.custom_combination'),
            },
          ]}
        />
        <MoneyField
          label={t('admin.drinks.basePrice')}
          minor={basePrice}
          currency={currency}
          onChange={setBasePrice}
        />
        {usesAllowance ? (
          <NumberField
            label={t('admin.drinks.allowance')}
            hint={t('admin.drinks.allowanceHint')}
            value={allowance}
            min={0}
            max={50}
            onChange={setAllowance}
          />
        ) : null}
        <MoneyField
          label={t('admin.drinks.minSpend')}
          hint={t('admin.drinks.minSpendHint')}
          minor={minSpend}
          currency={currency}
          onChange={setMinSpend}
        />
        <SwitchField
          label={t('admin.food.includesAdmission')}
          hint={t('admin.drinks.admissionHint')}
          value={admission}
          onChange={setAdmission}
        />
        <SwitchField
          label={t('admin.active')}
          hint={t('admin.activeHint')}
          value={active}
          onChange={setActive}
        />
        <div className="sm:col-span-2">
          <MediaField
            label={t('admin.media.tablePhoto')}
            hint={t('admin.media.tablePhotoHint')}
            value={mediaId}
            eventId={eventId}
            onChange={setMediaId}
            aspect="wide"
          />
        </div>
      </div>

      {usesIncluded ? null : (
        <p className="mt-5 rounded-md border border-line-subtle px-3 py-2 text-xs text-content-muted">
          {t('admin.drinks.bottlesNotApplicable')}
        </p>
      )}

      {usesIncluded ? (
      <div className="mt-5">
        <p className="text-caption uppercase tracking-wide text-content-muted">
          {t('admin.drinks.includedItems')}
        </p>
        <p className="mt-1 text-xs text-content-muted">{t('admin.drinks.includedItemsHint')}</p>
        <ul className="mt-3 grid gap-1.5 sm:grid-cols-2">
          {drinks.map((drink) => (
            <li
              key={drink.id}
              className="flex min-w-0 items-center justify-between gap-4 rounded-md border border-line-subtle px-3 py-2"
            >
              <span className="min-w-0 truncate text-sm text-content-primary">
                {drinkLabel(drink.name_i18n)}
              </span>
              <input
                type="number"
                min={0}
                max={50}
                value={included[drink.id] ?? 0}
                onChange={(event) => setQuantity(drink.id, Number(event.target.value))}
                className="w-16 shrink-0 rounded-md border border-line bg-bg-base px-2 py-1 text-right text-sm tabular-nums text-content-primary"
              />
            </li>
          ))}
        </ul>
      </div>
      ) : null}

      {usesIncluded && composition.length > 1 ? (
        <div className="mt-5">
          <p className="text-caption uppercase tracking-wide text-content-muted">
            {t('admin.drinks.includedOrder')}
          </p>
          <p className="mt-1 text-xs text-content-muted">
            {t('admin.drinks.includedOrderHint')}
          </p>
          <ol className="mt-3 grid gap-1.5">
            {composition.map((drinkId, index) => {
              const drink = drinks.find((entry) => entry.id === drinkId)
              return (
                <li
                  key={drinkId}
                  className="flex items-center gap-3 rounded-md border border-line-subtle px-3 py-2"
                >
                  <span className="w-6 shrink-0 text-caption tabular-nums text-content-muted">
                    {index + 1}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-sm text-content-primary">
                    {included[drinkId]} × {drink ? drinkLabel(drink.name_i18n) : drinkId}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={index === 0}
                    aria-label={t('admin.drinks.moveUp')}
                    onClick={() => move(drinkId, -1)}
                  >
                    ↑
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={index === composition.length - 1}
                    aria-label={t('admin.drinks.moveDown')}
                    onClick={() => move(drinkId, 1)}
                  >
                    ↓
                  </Button>
                </li>
              )
            })}
          </ol>
        </div>
      ) : null}

      <FormActions
        busy={busy}
        onCancel={onCancel}
        onSubmit={() =>
          onSubmit({
            name_i18n: name,
            description_i18n: description,
            media_id: mediaId,
            capacity_min: capacityMin ?? 1,
            capacity_max: capacityMax ?? 1,
            stock_quantity: stock,
            pricing_strategy: strategy,
            base_price_minor: basePrice ?? 0,
            included_bottles_allowance: allowance ?? 0,
            min_spend_minor: minSpend,
            includes_admission: admission,
            is_active: active,
            // L'ordre du tableau **est** la position enregistrée.
            included_items: composition.map((drink_item_id) => ({
              drink_item_id,
              quantity: included[drink_item_id] ?? 0,
            })),
          })
        }
      />
    </div>
  )
}

/**
 * Une formule de table : une composition à prix fixe, attachée à un type.
 *
 * Le client la choisit sur la table qu'il réserve ; son prix remplace alors
 * le prix de base du type (`table_pricing.py`). Le type se fixe à la
 * création — le serveur ne le modifie pas ensuite.
 */
function PackageForm({
  entry,
  tableTypes,
  drinks,
  nameOf,
  currency,
  busy,
  onSubmit,
  onCancel,
}: {
  entry: TablePackage | null
  tableTypes: TableType[]
  drinks: DrinkItem[]
  nameOf: (value: I18n) => string
  currency: string
  busy: boolean
  onSubmit: (payload: Record<string, unknown>) => void
  onCancel: () => void
}) {
  const { t } = useTranslation()
  const [typeId, setTypeId] = useState(entry?.table_type_id ?? tableTypes[0]?.id ?? '')
  const [name, setName] = useState<I18nValue>(entry?.name_i18n ?? {})
  const [description, setDescription] = useState<I18nValue>(entry?.description_i18n ?? {})
  const [priceMinor, setPriceMinor] = useState<number | null>(
    entry?.package_price_minor ?? null,
  )
  const [maxQuantity, setMaxQuantity] = useState<number | null>(entry?.max_quantity ?? null)
  const [active, setActive] = useState(entry?.is_active ?? true)
  // Les lignes gardent l'ordre de saisie : c'est celui de l'affichage public.
  const [lines, setLines] = useState<{ drink_item_id: string; quantity: number }[]>(() =>
    (entry?.items ?? []).map(({ drink_item_id, quantity }) => ({ drink_item_id, quantity })),
  )

  function setQuantity(drinkId: string, quantity: number) {
    setLines((current) => {
      if (quantity <= 0) return current.filter((line) => line.drink_item_id !== drinkId)
      if (!current.some((line) => line.drink_item_id === drinkId)) {
        return [...current, { drink_item_id: drinkId, quantity }]
      }
      return current.map((line) =>
        line.drink_item_id === drinkId ? { ...line, quantity } : line,
      )
    })
  }

  return (
    <div>
      <div className="grid gap-5 sm:grid-cols-2">
        {entry ? (
          <Field
            label={t('admin.drinks.packageTableType')}
            hint={t('admin.drinks.packageTableTypeFixed')}
          >
            <p className="py-2 text-sm text-content-primary">
              {nameOf(tableTypes.find((type) => type.id === typeId)?.name_i18n ?? {})}
            </p>
          </Field>
        ) : (
          <SelectField
            label={t('admin.drinks.packageTableType')}
            hint={t('admin.drinks.packageTableTypeHint')}
            value={typeId}
            onChange={setTypeId}
            options={tableTypes.map((type) => ({
              value: type.id,
              label: nameOf(type.name_i18n),
            }))}
          />
        )}
        <I18nField label={t('admin.food.name')} value={name} onChange={setName} />
        <I18nField
          label={t('admin.food.descriptionField')}
          value={description}
          onChange={setDescription}
        />
        <MoneyField
          label={t('admin.drinks.packagePrice')}
          hint={t('admin.drinks.packagePriceHint')}
          minor={priceMinor}
          currency={currency}
          onChange={setPriceMinor}
        />
        <NumberField
          label={t('admin.drinks.packageMax')}
          hint={t('admin.drinks.packageMaxHint')}
          value={maxQuantity}
          min={1}
          max={500}
          onChange={setMaxQuantity}
        />
        <SwitchField
          label={t('admin.active')}
          hint={t('admin.activeHint')}
          value={active}
          onChange={setActive}
        />
      </div>

      <div className="mt-5">
        <p className="text-caption uppercase tracking-wide text-content-muted">
          {t('admin.drinks.packageItems')}
        </p>
        <p className="mt-1 text-xs text-content-muted">{t('admin.drinks.packageItemsHint')}</p>
        <ul className="mt-3 grid gap-1.5 sm:grid-cols-2">
          {drinks.map((drink) => (
            <li
              key={drink.id}
              className="flex min-w-0 items-center justify-between gap-4 rounded-md border border-line-subtle px-3 py-2"
            >
              <span className="min-w-0 truncate text-sm text-content-primary">
                {nameOf(drink.name_i18n)}
              </span>
              <input
                type="number"
                min={0}
                max={50}
                aria-label={nameOf(drink.name_i18n)}
                value={lines.find((line) => line.drink_item_id === drink.id)?.quantity ?? 0}
                onChange={(event) => setQuantity(drink.id, Number(event.target.value))}
                className="w-16 shrink-0 rounded-md border border-line bg-bg-base px-2 py-1 text-right text-sm tabular-nums text-content-primary"
              />
            </li>
          ))}
        </ul>
      </div>

      <FormActions
        // Le serveur exige un type de table et au moins une bouteille.
        busy={busy || lines.length === 0 || !typeId}
        onCancel={onCancel}
        onSubmit={() =>
          onSubmit({
            // Le type ne part qu'à la création : le serveur ne le modifie pas.
            ...(entry ? {} : { table_type_id: typeId }),
            name_i18n: name,
            description_i18n: description,
            package_price_minor: priceMinor ?? 0,
            max_quantity: maxQuantity,
            is_active: active,
            items: lines,
          })
        }
      />
    </div>
  )
}

function PhysicalTableForm({
  table,
  tableTypes,
  typeLabel,
  busy,
  onSubmit,
  onCancel,
}: {
  table: PhysicalTable | null
  tableTypes: TableType[]
  typeLabel: (value: I18n) => string
  busy: boolean
  onSubmit: (payload: Record<string, unknown>) => void
  onCancel: () => void
}) {
  const { t } = useTranslation()
  const [number, setNumber] = useState(table?.table_number ?? '')
  const [name, setName] = useState(table?.name ?? '')
  const [typeId, setTypeId] = useState(table?.table_type_id ?? '')
  const [zone, setZone] = useState(table?.zone ?? '')
  const [capacity, setCapacity] = useState<number | null>(table?.capacity ?? 4)
  const [status, setStatus] = useState<PhysicalTableStatus>(table?.status ?? 'available')

  return (
    <div>
      <div className="grid gap-5 sm:grid-cols-2">
        <TextField
          label={t('admin.drinks.tableNumber')}
          hint={t('admin.drinks.tableNumberHint')}
          value={number}
          onChange={setNumber}
        />
        <TextField
          label={t('admin.drinks.tableName')}
          hint={t('admin.drinks.tableNameHint')}
          placeholder={t('admin.drinks.tableNamePlaceholder')}
          value={name}
          onChange={setName}
        />
        <SelectField
          label={t('admin.drinks.tableType')}
          value={typeId}
          onChange={setTypeId}
          options={[
            { value: '', label: t('admin.drinks.noType') },
            ...tableTypes.map((type) => ({
              value: type.id,
              label: typeLabel(type.name_i18n),
            })),
          ]}
        />
        <TextField label={t('admin.drinks.zone')} value={zone} onChange={setZone} />
        <NumberField
          label={t('admin.drinks.capacity')}
          value={capacity}
          min={1}
          max={50}
          onChange={setCapacity}
        />
        <SelectField<PhysicalTableStatus>
          label={t('admin.drinks.tableStatus')}
          value={status}
          onChange={setStatus}
          options={[
            { value: 'available', label: t('admin.drinks.status.available') },
            { value: 'blocked', label: t('admin.drinks.status.blocked') },
            { value: 'out_of_service', label: t('admin.drinks.status.out_of_service') },
          ]}
        />
      </div>
      <FormActions
        busy={busy || !number.trim()}
        onCancel={onCancel}
        onSubmit={() =>
          onSubmit({
            table_number: number.trim(),
            name: name.trim() || null,
            table_type_id: typeId || null,
            zone: zone || null,
            capacity: capacity ?? 1,
            status,
          })
        }
      />
    </div>
  )
}
